import React, { useState, useRef } from 'react'
import { T, Sheet, Avatar, fmtOdds } from './ui'
import { checkNFLSlip } from './api'
import { playText, teamNick } from './plain'
import { track } from './track'
import { shareSlip } from './share'

// Check my slip — drop a sportsbook screenshot or paste the slip text; every leg is matched to the
// model and graded (model % vs the book's price), plus the whole-ticket chance vs the payout.
// Free to run: the screenshot is read ON THE PHONE by Tesseract (open source, loaded from a public
// CDN only when someone uses it), and only the resulting text is sent to us. Research only.

const TESS_SRC = 'https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js'
let _tess = null
const loadTesseract = () => _tess || (_tess = new Promise((ok, fail) => {
  if (window.Tesseract) return ok(window.Tesseract)
  const s = document.createElement('script')
  s.src = TESS_SRC; s.async = true
  s.onload = () => (window.Tesseract ? ok(window.Tesseract) : fail(new Error('reader unavailable')))
  s.onerror = () => { _tess = null; fail(new Error('reader unavailable')) }
  document.head.appendChild(s)
}))

const pctS = p => (p == null ? '—' : `${Math.round(p * 100)}%`)
const oneIn = p => (p > 0 ? `about 1 in ${Math.max(1, Math.round(1 / p))}` : '')
// Two plain questions per leg: how's the matchup, and are the odds good? (A TD parlay is a long
// shot by design, so "weakest leg" / "priced in" jargon reads like a warning it isn't.)
const VERDICT = {
  value: { txt: '✅ Good price', c: T.green },
  priced_in: { txt: '👍 Fair price', c: T.muted2 },
  overpriced: { txt: '❌ Short price', c: T.red },
  model_only: { txt: 'No price to compare', c: T.muted },
}
function matchupGrade(m) {
  if (!m || !m.rank) return null
  const of = m.of || 32
  return m.rank <= 10 ? { txt: '🟢 Great matchup', c: T.green }
    : m.rank > of - 10 ? { txt: '🔴 Tough matchup', c: T.red } : { txt: '⚪ OK matchup', c: T.muted2 }
}
// Whole ticket: does the payout pay at least what the model says it's worth?
function ticketOdds(t, pay) {
  if (t == null || pay == null) return null
  const r = t / pay
  return r >= 1.15 ? { txt: '✅ Good odds', c: T.green, why: 'The payout is better than the model thinks this ticket deserves.' }
    : r >= 0.85 ? { txt: '👍 Fair odds', c: T.text, why: 'The payout matches the model’s chances: no edge either way.' }
    : { txt: '❌ Short odds', c: T.red, why: 'The payout is lower than the model thinks this ticket deserves.' }
}

const ord = n => { const v = n % 100; return n + (['th', 'st', 'nd', 'rd'][(v - 20) % 10] || ['th', 'st', 'nd', 'rd'][v] || 'th') }
// "Falcons: 3rd-softest matchup for TDs to starting TEs (4 in 6 games)" / "...20th-toughest..."
function matchupFact(m) {
  if (!m || !m.rank) return null
  const of = m.of || 32, soft = m.rank <= Math.ceil(of / 2)
  const pos = soft ? `${ord(m.rank)}-softest` : `${ord(of + 1 - m.rank)}-toughest`
  const color = m.rank <= 10 ? T.green : m.rank > of - 10 ? T.red : T.muted
  return { text: `${teamNick(m.opp)}: ${pos} matchup for ${m.what}${m.detail ? ` (${m.detail})` : ''}`, color }
}

function legText(l) {
  if (l.prop_type === 'anytime_td') return 'Anytime TD'
  return playText(l.market, l.line, l.side)
}

function Leg({ l, weakest, onPlayer }) {
  const v = VERDICT[l.verdict] || VERDICT.model_only
  if (l.status !== 'ok') {
    return (
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 10px', marginTop: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 800 }}>{l.player}</div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{l.market} · not on this week's board, so we can't grade it</div>
      </div>
    )
  }
  return (
    <button onClick={() => onPlayer && onPlayer({ full_name: l.player, gsis_id: l.gsis_id, pos: l.pos, team: l.team, opp: l.opp,
      headshot: l.headshot, status: 'active', prop_type: l.prop_type, market: l.market })}
      style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', cursor: 'pointer', color: T.text,
        background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 10px', marginTop: 6 }}>
      <Avatar url={l.headshot} name={l.player} team={l.team} size={30} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800 }}>{l.player}</div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{legText(l)}{l.team ? ` · ${l.team}` : ''}</div>
        {(() => { const f = matchupFact(l.matchup); return f ? (
          <div style={{ fontSize: 10.5, color: T.muted2, marginTop: 3, lineHeight: 1.35 }}>
            <span style={{ color: f.color, fontWeight: 900 }}>● </span>{f.text}</div>) : null })()}
        {l.pos_rank && l.pos_rank.of > 1 && (
          <div style={{ fontSize: 10.5, color: T.muted2, marginTop: 2 }}>
            {pctS(l.prob)} shot · <b style={{ color: l.pos_rank.rank <= Math.max(3, Math.round(l.pos_rank.of * 0.15)) ? T.green : T.text }}>
              {ord(l.pos_rank.rank)}-best {l.pos_rank.pos}</b> on the board this week</div>)}
        {l.line_assumed && <div style={{ fontSize: 10, color: T.amber, marginTop: 2 }}>Couldn’t read the line, so we used ours. Edit the text if it’s different.</div>}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
          {[matchupGrade(l.matchup), v].filter(Boolean).map((c, k) => (
            <span key={k} style={{ fontSize: 10.5, fontWeight: 800, color: c.c, border: `1px solid ${c.c}66`,
              background: `${c.c}18`, borderRadius: 6, padding: '2px 6px' }}>{c.txt}</span>))}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: T.mono, fontSize: 16, fontWeight: 900 }}>{pctS(l.prob)}</div>
        <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>
          {l.book_prob != null ? `${pctS(l.book_prob)} ${l.price_src === 'slip' ? 'your slip' : 'book'} (${fmtOdds(l.book_odds)})` : 'model'}</div>
      </div>
    </button>
  )
}

export function SlipCheck({ season, week, onClose, onPlayer }) {
  const [mode, setMode] = useState('shot')
  const [text, setText] = useState('')
  const [img, setImg] = useState(null)
  const [reading, setReading] = useState('')
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState(null)
  const [err, setErr] = useState('')
  const fileRef = useRef(null)

  const check = async (t) => {
    const body = (t ?? text).trim()
    if (!body) { setErr('Paste your slip first.'); return }
    setBusy(true); setErr(''); setRes(null)
    const r = await checkNFLSlip(body, season, week)
    setBusy(false)
    if (r._error) { setErr('Couldn’t check the slip right now. Try again in a minute.'); return }
    if (!r.legs?.length) { setErr('We couldn’t find any legs we recognize. Check the player names in the text below, or paste the slip instead.'); setMode('paste') }
    track('slip_check', `${r.legs?.length || 0}:${r.all_matched ? 'all' : 'partial'}`)
    setRes(r)
  }

  const onFile = async (f) => {
    if (!f) return
    setErr(''); setRes(null)
    setImg(URL.createObjectURL(f))
    setReading('Loading the reader…')
    try {
      const Tess = await loadTesseract()
      setReading('Reading your slip on this phone…')
      const { data } = await Tess.recognize(f, 'eng')
      const t = (data?.text || '').trim()
      setText(t); setReading('')
      track('slip_ocr', t ? 'ok' : 'empty')
      if (t) await check(t)
      else setErr('We couldn’t read that image. Try a clearer screenshot, or paste the text.')
    } catch (_) {
      setReading('')
      setErr('The screenshot reader didn’t load. Paste the slip text instead.'); setMode('paste')
    }
  }

  const tab = (k, lbl) => (
    <button onClick={() => { setMode(k); setErr('') }} aria-pressed={mode === k} style={{ cursor: 'pointer', border: 'none', borderRadius: 8,
      padding: '9px 0', fontSize: 12.5, fontWeight: 800, color: mode === k ? '#04140D' : T.muted, background: mode === k ? T.green : 'transparent' }}>{lbl}</button>
  )
  const graded = (res?.legs || []).filter(l => l.status === 'ok')

  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '6px 16px 26px' }}>
        <div style={{ fontSize: 19, fontWeight: 900 }}>🧾 Check my slip</div>
        <div style={{ fontSize: 12, color: T.muted2, marginTop: 3, lineHeight: 1.45 }}>
          Drop a screenshot or paste your slip. We match every leg to the model and show the real odds.</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, background: T.card, border: `1px solid ${T.border}`,
          borderRadius: 11, padding: 3, marginTop: 12 }}>
          {tab('shot', '📷 Screenshot')}{tab('paste', '📋 Paste text')}
        </div>

        {mode === 'shot' ? (
          <div style={{ marginTop: 10 }}>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onFile(e.target.files?.[0])} />
            <button onClick={() => fileRef.current?.click()} disabled={!!reading || busy} style={{ width: '100%', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', color: T.text, background: T.card,
              border: `1px dashed ${T.green}88`, borderRadius: 11, padding: 10 }}>
              {img ? <img src={img} alt="Your slip" style={{ width: 70, maxHeight: 110, objectFit: 'cover', borderRadius: 6 }} />
                : <span style={{ fontSize: 26 }}>📷</span>}
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800 }}>{img ? 'Choose a different screenshot' : 'Choose a slip screenshot'}</span>
                <span style={{ display: 'block', fontSize: 11, color: reading ? T.green : T.muted, marginTop: 3 }}>
                  {reading || 'Read on your phone. The image is never uploaded.'}</span>
              </span>
            </button>
            {text && !reading && (
              <div style={{ fontSize: 10.5, color: T.muted, marginTop: 6 }}>Misread something?{' '}
                <button onClick={() => setMode('paste')} style={{ background: 'none', border: 'none', color: T.green, fontWeight: 800,
                  fontSize: 10.5, cursor: 'pointer', padding: 0 }}>Edit the text</button></div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 6, lineHeight: 1.4 }}>
              iPhone tip: open your slip screenshot, press and hold the text, tap <b>Copy</b>, then paste here.</div>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={7}
              placeholder={'Derrick Henry | Anytime TD Scorer\n2+ | Matthew Stafford Passing TDs\nA.J. Brown Under 69.5 Receiving Yards'}
              style={{ width: '100%', background: T.card, color: T.text, border: `1px solid ${T.green}88`, borderRadius: 10, padding: 9,
                fontSize: 12, fontFamily: T.mono, resize: 'vertical', boxSizing: 'border-box' }} />
            <button onClick={() => check()} disabled={busy} style={{ width: '100%', marginTop: 8, padding: '12px 0', fontSize: 14.5, fontWeight: 900,
              background: T.green, color: '#04140D', border: 'none', borderRadius: 10, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Checking…' : 'Check my slip'}</button>
          </div>
        )}

        {err && <div style={{ fontSize: 12, color: T.amber, marginTop: 10, lineHeight: 1.4 }}>{err}</div>}
        {busy && mode === 'shot' && <div style={{ fontSize: 12, color: T.muted, marginTop: 10 }}>Checking…</div>}

        {res && res.legs?.length > 0 && (
          <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 10, marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Your slip · {res.legs.length} leg{res.legs.length === 1 ? '' : 's'}{res.parlay_odds != null ? ` · ${fmtOdds(res.parlay_odds)}` : ''}</div>
            {res.legs.map((l, i) => <Leg key={i} l={l} weakest={graded.length > 1 && l.player === res.weakest && l.status === 'ok'} onPlayer={onPlayer} />)}

            {res.ticket_prob != null && graded.length > 1 && (
              <div style={{ display: 'grid', gridTemplateColumns: res.payout_prob != null ? '1fr 1fr' : '1fr', gap: 6, marginTop: 10 }}>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 9, padding: 8, textAlign: 'center' }}>
                  <div style={{ fontFamily: T.mono, fontSize: 19, fontWeight: 900,
                    color: res.payout_prob != null && res.ticket_prob > res.payout_prob ? T.green : T.text }}>{pctS(res.ticket_prob)}</div>
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>model: whole ticket hits<br />({oneIn(res.ticket_prob)})</div>
                </div>
                {res.payout_prob != null && (
                  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 9, padding: 8, textAlign: 'center' }}>
                    <div style={{ fontFamily: T.mono, fontSize: 19, fontWeight: 900 }}>{pctS(res.payout_prob)}</div>
                    <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>what {fmtOdds(res.parlay_odds)} implies<br />({oneIn(res.payout_prob)})</div>
                  </div>
                )}
              </div>
            )}

            {graded.length > 1 && (() => {
              const to = ticketOdds(res.ticket_prob, res.payout_prob)
              const great = graded.filter(l => (matchupGrade(l.matchup) || {}).txt === '🟢 Great matchup').length
              const tough = graded.filter(l => (matchupGrade(l.matchup) || {}).txt === '🔴 Tough matchup').length
              return (
                <div style={{ fontSize: 11.5, color: T.text, lineHeight: 1.45, marginTop: 10, background: 'linear-gradient(110deg,#0C1F18,#0b1420)',
                  border: `1px solid ${T.green}55`, borderRadius: 10, padding: '9px 10px' }}>
                  {to && <div style={{ fontSize: 13.5, fontWeight: 900, color: to.c, marginBottom: 3 }}>{to.txt}</div>}
                  {to ? `${to.why} ` : ''}
                  {great || tough ? `Matchups: ${great} great${tough ? `, ${tough} tough` : ''} of ${graded.length}. ` : ''}
                  {res.same_game ? 'Legs from the same game tend to move together, so the true odds can differ. ' : ''}
                </div>)
            })()}

            {graded.length > 0 && (
              <button onClick={() => { track('slip_share', String(graded.length))
                const to = ticketOdds(res.ticket_prob, res.payout_prob)
                shareSlip(res, { matchup: l => (matchupGrade(l.matchup) || {}).txt, price: l => (VERDICT[l.verdict] || {}).txt,
                  ticket: to ? to.txt : '' }) }}
                style={{ width: '100%', marginTop: 10, padding: '11px 0', borderRadius: 10, cursor: 'pointer', fontWeight: 900,
                  fontSize: 13.5, color: '#04140D', background: T.green, border: 'none' }}>⤴ Share my slip read</button>
            )}

            {res.adjustments?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Better options</div>
                {res.adjustments.map((a, i) => (
                  <div key={i} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 10px', marginTop: 6 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800 }}>{a.player}
                      <span style={{ fontWeight: 600, color: T.muted }}> · {a.reason === 'tough_matchup' ? '🔴 tough matchup' : '❌ short price'}</span></div>
                    {a.swaps?.length > 0 ? (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ fontSize: 10.5, color: T.muted }}>{a.prop_type === 'anytime_td'
                          ? 'Similar odds, better matchup:' : 'Same market, priced better:'}</div>
                        {a.swaps.map((w, k) => (
                          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, marginTop: 4,
                            background: T.card2, border: `1px solid ${T.green}44`, borderRadius: 8, padding: '6px 8px' }}>
                            <span style={{ minWidth: 0 }}><b>{w.player}</b> <span style={{ color: T.muted }}>
                              {w.prop_type === 'anytime_td' ? 'Anytime TD' : playText(w.market, w.line)} · {w.why}</span>
                              {(() => { const f = matchupFact(w.matchup); return f ? (
                                <span style={{ display: 'block', fontSize: 10.5, color: T.muted2, marginTop: 2 }}>
                                  <span style={{ color: f.color, fontWeight: 900 }}>● </span>{f.text}</span>) : null })()}</span>
                            <span style={{ fontFamily: T.mono, fontWeight: 900, color: T.green, flexShrink: 0 }}>
                              {pctS(w.prob)}{w.book_odds != null ? <span style={{ color: T.muted, fontWeight: 600 }}> ({fmtOdds(w.book_odds)})</span> : null}</span>
                          </div>
                        ))}
                      </div>
                    ) : <div style={{ fontSize: 10.5, color: T.muted, marginTop: 4 }}>No similar option with a better matchup right now.</div>}
                  </div>
                ))}
                <div style={{ fontSize: 10, color: T.faint, marginTop: 6 }}>Ideas from the model, not advice. Check the price at your book.</div>
              </div>
            )}

            {res.unmatched?.length > 0 && (
              <div style={{ fontSize: 11, color: T.amber, marginTop: 8, lineHeight: 1.4 }}>
                Not graded: {res.unmatched.join(' · ')}. That player isn’t on this week’s board (often out or on reserve), or the name was misread.</div>
            )}
          </div>
        )}
        <div style={{ fontSize: 9.5, color: T.faint, marginTop: 12, textAlign: 'center', lineHeight: 1.5 }}>
          Research, not advice · 21+ · 1-800-GAMBLER · screenshots are read on your phone and never uploaded</div>
      </div>
    </Sheet>
  )
}

// Entry button + the sheet it opens. Drop anywhere (Home, Play Lab).
export function SlipCheckButton({ season, week, onPlayer, compact }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button onClick={() => { track('slip_check', 'open'); setOpen(true) }} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12,
        cursor: 'pointer', textAlign: 'left', color: T.text, background: T.card, border: `1px solid ${T.border2}`, borderRadius: 14,
        padding: compact ? '11px 13px' : '13px 15px', marginTop: 8 }}>
        <span style={{ fontSize: 24, flexShrink: 0 }}>🧾</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 15, fontWeight: 900 }}>Check my slip</span>
          <span style={{ display: 'block', fontSize: 11.5, color: T.muted2, marginTop: 2, lineHeight: 1.4 }}>
            Drop a screenshot or paste a slip and see each leg graded by the model.</span>
        </span>
        <span style={{ color: T.green, fontWeight: 900, fontSize: 16 }}>›</span>
      </button>
      {open && <SlipCheck season={season} week={week} onClose={() => setOpen(false)} onPlayer={onPlayer} />}
    </>
  )
}
